import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BookOpen, ArrowLeft, Search, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CourseLibrary({ user }) {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/courses`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      } else {
        console.error('Failed to load courses:', response.status);
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const handleArchive = async (e, courseId, isCurrentlyArchived) => {
    e.stopPropagation(); // Prevent card click navigation
    
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API}/courses/${courseId}/archive`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadCourses(); // Reload courses to reflect change
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to archive course');
      }
    } catch (error) {
      console.error('Error archiving course:', error);
      toast.error('Failed to archive course');
    }
  };

  // Filter courses based on search and archived status
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(search.toLowerCase()) ||
      course.description.toLowerCase().includes(search.toLowerCase());
    
    // For admin, filter by showArchived toggle
    if (user?.role === 'admin') {
      if (showArchived) {
        return matchesSearch && course.archived;
      }
      return matchesSearch && !course.archived;
    }
    
    // Non-admins only see non-archived courses (already filtered by backend)
    return matchesSearch;
  });

  const archivedCount = courses.filter(c => c.archived).length;

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9] p-2 sm:p-3"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-2 sm:gap-3">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-lg sm:text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>Course Library</h1>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative w-full sm:max-w-xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A3B18A]" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Search courses..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="course-search-input"
              className="pl-12 bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
            />
          </div>
          
          {/* Admin toggle for archived courses */}
          {user?.role === 'admin' && archivedCount > 0 && (
            <Button
              variant={showArchived ? "default" : "outline"}
              onClick={() => setShowArchived(!showArchived)}
              data-testid="toggle-archived-btn"
              className={showArchived 
                ? "bg-[#D4A373] text-white hover:bg-[#c49366]" 
                : "border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373]/10"
              }
            >
              <Archive className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {showArchived ? `Showing Archived (${archivedCount})` : `View Archived (${archivedCount})`}
            </Button>
          )}
        </div>

        {filteredCourses.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#588157]">
              {showArchived ? "No archived courses found" : "No courses found"}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredCourses.map((course) => (
              <Card
                key={course.course_id}
                data-testid={`course-card-${course.course_id}`}
                className={`bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl overflow-hidden cursor-pointer relative ${course.archived ? 'opacity-75' : ''}`}
                onClick={() => navigate(`/courses/${course.course_id}`)}
              >
                {/* Archived Badge */}
                {course.archived && (
                  <div className="absolute top-3 right-3 z-10 bg-[#D4A373] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow-md">
                    <Archive className="w-3 h-3" strokeWidth={2} />
                    Archived
                  </div>
                )}
                
                {course.thumbnail && (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className={`w-full h-36 sm:h-48 object-cover ${course.archived ? 'grayscale' : ''}`}
                  />
                )}
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                    {course.title}
                  </h3>
                  <p className="text-sm sm:text-base text-[#588157] mb-4 line-clamp-3">{course.description}</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs sm:text-sm text-[#A3B18A]">{course.duration_weeks} weeks</span>
                    <div className="flex items-center gap-2">
                      {/* Archive/Unarchive button - Admin only */}
                      {user?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => handleArchive(e, course.course_id, course.archived)}
                          data-testid={`archive-btn-${course.course_id}`}
                          className={course.archived 
                            ? "border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white text-xs sm:text-sm"
                            : "border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white text-xs sm:text-sm"
                          }
                          title={course.archived ? "Restore course" : "Archive course"}
                        >
                          {course.archived ? (
                            <>
                              <ArchiveRestore className="w-4 h-4 mr-1" strokeWidth={1.5} />
                              Restore
                            </>
                          ) : (
                            <>
                              <Archive className="w-4 h-4 mr-1" strokeWidth={1.5} />
                              Archive
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 active:scale-95 font-medium rounded-lg text-xs sm:text-sm"
                        data-testid={`view-details-btn-${course.course_id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
